/**
 * A cohort's COMPLETION RULES tab (M1): registry-driven editor +
 * roster completion matrix + manual marking. All gated by
 * `academy.completion_rules`. Server-authoritative reads (outside of sync).
 */
import { type Component, createEffect, createResource, createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import {
  COMPLETION_CRITERIA,
  COMPLETION_CRITERIA_META,
  type CompletionConfig,
  type CompletionCriterionKey,
  type CompletionNode,
  recommendedDefaultConfig,
} from "@space/shared";

import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { InfoHint } from "@/components/ui/info-hint";
import { showToast } from "@/components/ui/toast";
import { useI18n } from "@/providers/i18n-provider";
import { FeatureGate } from "@/features/flags/feature-gate";
import {
  fetchCohortCompletionConfig,
  fetchCompletionReport,
  markCompletion,
  saveCohortCompletionConfig,
} from "./api";

const field =
  "w-full rounded-lg border-2 border-border bg-field-background px-3 py-2 text-sm focus-visible:border-primary focus-visible:outline-none";

// ── Editor's internal model (root + one level of groups) ─────────────────

interface Row {
  key: CompletionCriterionKey;
  params: Record<string, number | undefined>;
}
type EditorItem =
  | { type: "criterion"; row: Row }
  | { type: "group"; mode: "all" | "any"; rows: Row[] };

const newRow = (key: CompletionCriterionKey = "min_final_grade"): Row => ({
  key,
  params: {},
});

// Safe coercions (no type-assertions) of <Select> values (string).
const MODES = ["all", "any"] as const;
const asMode = (v: string): "all" | "any" => MODES.find((m) => m === v) ?? "all";
const asKey = (v: string): CompletionCriterionKey =>
  COMPLETION_CRITERIA.find((k) => k === v) ?? "min_final_grade";
const groupNode = (mode: "all" | "any", children: CompletionNode[]): CompletionNode =>
  mode === "all" ? { type: "all", children } : { type: "any", children };

function critNode(row: Row): CompletionNode {
  const params: Record<string, number> = {};
  for (const spec of COMPLETION_CRITERIA_META[row.key].params) {
    const v = row.params[spec.key];
    if (typeof v === "number" && !Number.isNaN(v)) {
      params[spec.key] = v;
    }
  }
  return { type: "criterion", key: row.key, params };
}

function itemsToConfig(mode: "all" | "any", items: EditorItem[]): CompletionConfig | null {
  const children: CompletionNode[] = items
    .map((it) =>
      it.type === "criterion" ? critNode(it.row) : groupNode(it.mode, it.rows.map(critNode)),
    )
    .filter((n) => n.type === "criterion" || n.children.length > 0);
  if (children.length === 0) {
    return null;
  }
  return { root: groupNode(mode, children) };
}

function rowFromNode(n: CompletionNode): Row | null {
  if (n.type !== "criterion") {
    return null;
  }
  const params: Record<string, number> = {};
  for (const [k, v] of Object.entries(n.params ?? {})) {
    if (typeof v === "number") {
      params[k] = v;
    }
  }
  return { key: asKey(n.key), params };
}

function configToItems(cfg: CompletionConfig | null): {
  mode: "all" | "any";
  items: EditorItem[];
} {
  if (!cfg || cfg.root.type === "criterion") {
    const row = cfg ? rowFromNode(cfg.root) : null;
    return { mode: "all", items: row ? [{ type: "criterion", row }] : [] };
  }
  const items: EditorItem[] = [];
  for (const child of cfg.root.children) {
    if (child.type === "criterion") {
      const row = rowFromNode(child);
      if (row) {
        items.push({ type: "criterion", row });
      }
    } else {
      const rows = child.children.map(rowFromNode).filter((r): r is Row => r !== null);
      if (rows.length > 0) {
        items.push({ type: "group", mode: child.type, rows });
      }
    }
  }
  return { mode: cfg.root.type, items };
}

// ── Editor ─────────────────────────────────────────────────────────────────────

const CriterionRow: Component<{
  row: Row;
  onChange: (row: Row) => void;
  onRemove: () => void;
}> = (props) => {
  const { t } = useI18n();
  const meta = () => COMPLETION_CRITERIA_META[props.row.key];
  return (
    <div class="flex flex-wrap items-center gap-2" data-testid="completion-rule-row">
      <div class="min-w-48">
        <Select
          aria-label={t(msg`Criterion`)}
          value={props.row.key}
          onChange={(v) => {
            props.onChange({ key: asKey(v), params: {} });
          }}
          options={COMPLETION_CRITERIA.map((k) => ({
            value: k,
            label: COMPLETION_CRITERIA_META[k].name,
          }))}
        />
      </div>
      <InfoHint>{meta().description}</InfoHint>
      <For each={meta().params}>
        {(spec) => (
          <label class="flex items-center gap-1 text-xs text-muted-foreground">
            {spec.label}
            <input
              type="number"
              class={`${field} w-24`}
              data-testid={`completion-param-${spec.key}`}
              value={props.row.params[spec.key] ?? spec.default ?? ""}
              onInput={(e) => {
                const raw = e.currentTarget.value;
                const num = raw === "" ? undefined : Number(raw);
                props.onChange({
                  ...props.row,
                  params: { ...props.row.params, [spec.key]: num },
                });
              }}
            />
          </label>
        )}
      </For>
      <Button variant="ghost" size="sm" onClick={props.onRemove} aria-label={t(msg`Remove`)}>
        ✕
      </Button>
    </div>
  );
};

const CompletionRulesEditor: Component<{
  cohortId: string;
  onSaved?: () => void;
}> = (props) => {
  const { t } = useI18n();
  const [config] = createResource(() => props.cohortId, fetchCohortCompletionConfig);
  const [mode, setMode] = createSignal<"all" | "any">("all");
  const [items, setItems] = createSignal<EditorItem[]>([]);
  const [hydrated, setHydrated] = createSignal(false);
  const [saving, setSaving] = createSignal(false);

  // Hydrates once from the effective config (only if explicit).
  createEffect(() => {
    const c = config();
    if (!c || hydrated()) {
      return;
    }
    if (c.configured) {
      const { mode: m, items: its } = configToItems(c.config);
      setMode(m);
      setItems(its);
    }
    setHydrated(true);
  });

  const updateItem = (idx: number, next: EditorItem) =>
    setItems((xs) => xs.map((x, i) => (i === idx ? next : x)));
  const removeItem = (idx: number) => setItems((xs) => xs.filter((_, i) => i !== idx));

  const applyRecommended = () => {
    const { mode: m, items: its } = configToItems(recommendedDefaultConfig());
    setMode(m);
    setItems(its);
  };

  const save = async () => {
    setSaving(true);
    try {
      const cfg = itemsToConfig(mode(), items());
      await saveCohortCompletionConfig(props.cohortId, cfg);
      showToast(cfg ? t(msg`Completion rules saved`) : t(msg`Completion rules cleared`), "success");
      props.onSaved?.();
    } catch {
      showToast(t(msg`Could not save rules`), "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div class="space-y-4" data-testid="completion-rules-editor">
      <div class="flex flex-wrap items-center gap-2">
        <span class="text-sm font-medium">{t(msg`A student passes when`)}</span>
        <div class="w-40">
          <Select
            aria-label={t(msg`Combination`)}
            value={mode()}
            onChange={(v) => setMode(asMode(v))}
            options={[
              { value: "all", label: t(msg`all of these`) },
              { value: "any", label: t(msg`any of these`) },
            ]}
          />
        </div>
      </div>

      <div class="space-y-3">
        <For each={items()}>
          {(item, idx) => (
            <Show
              when={item.type === "criterion"}
              fallback={
                <div
                  class="rounded-lg border-2 border-dashed border-border p-3"
                  data-testid="completion-group"
                >
                  <Show when={item.type === "group" ? item : null}>
                    {(g) => (
                      <div class="space-y-2">
                        <div class="flex items-center gap-2 text-xs text-muted-foreground">
                          {t(msg`Sub-group — satisfied when`)}
                          <div class="w-32">
                            <Select
                              aria-label={t(msg`Sub-group combination`)}
                              value={g().mode}
                              onChange={(v) =>
                                updateItem(idx(), {
                                  ...g(),
                                  mode: asMode(v),
                                })
                              }
                              options={[
                                { value: "all", label: t(msg`all`) },
                                { value: "any", label: t(msg`any`) },
                              ]}
                            />
                          </div>
                        </div>
                        <For each={g().rows}>
                          {(row, ridx) => (
                            <CriterionRow
                              row={row}
                              onChange={(r) =>
                                updateItem(idx(), {
                                  ...g(),
                                  rows: g().rows.map((x, i) => (i === ridx() ? r : x)),
                                })
                              }
                              onRemove={() =>
                                updateItem(idx(), {
                                  ...g(),
                                  rows: g().rows.filter((_, i) => i !== ridx()),
                                })
                              }
                            />
                          )}
                        </For>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            updateItem(idx(), {
                              ...g(),
                              rows: [...g().rows, newRow()],
                            })
                          }
                        >
                          {t(msg`Add criterion to group`)}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => removeItem(idx())}>
                          {t(msg`Remove group`)}
                        </Button>
                      </div>
                    )}
                  </Show>
                </div>
              }
            >
              <Show when={item.type === "criterion" ? item.row : null}>
                {(row) => (
                  <CriterionRow
                    row={row()}
                    onChange={(r) => updateItem(idx(), { type: "criterion", row: r })}
                    onRemove={() => removeItem(idx())}
                  />
                )}
              </Show>
            </Show>
          )}
        </For>
      </div>

      <div class="flex flex-wrap gap-2">
        <Button
          variant="secondary"
          size="sm"
          data-testid="completion-rule-add-criterion"
          onClick={() => setItems((xs) => [...xs, { type: "criterion", row: newRow() }])}
        >
          {t(msg`Add criterion`)}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          data-testid="completion-rule-add-group"
          onClick={() =>
            setItems((xs) => [...xs, { type: "group", mode: "all", rows: [newRow()] }])
          }
        >
          {t(msg`Add sub-group`)}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          data-testid="completion-rule-recommended"
          onClick={applyRecommended}
        >
          {t(msg`Apply recommended`)}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          data-testid="completion-rule-clear"
          onClick={() => setItems([])}
        >
          {t(msg`Clear rules`)}
        </Button>
        <Button data-testid="completion-rule-save" disabled={saving()} onClick={save}>
          {t(msg`Save rules`)}
        </Button>
      </div>
      <p class="text-xs text-muted-foreground">
        {t(
          msg`With no rules, a cohort certifies whoever reaches the passing grade (the classic behavior).`,
        )}
      </p>
    </div>
  );
};

// ── Roster report ──────────────────────────────────────────────────────

const CompletionReport: Component<{ cohortId: string; reloadKey: number }> = (props) => {
  const { t } = useI18n();
  const [report, { refetch }] = createResource(
    () => ({ id: props.cohortId, reload: props.reloadKey }),
    (src) => fetchCompletionReport(src.id),
  );
  const [marking, setMarking] = createSignal<string | null>(null);

  const toggleMark = async (userId: string, complete: boolean) => {
    setMarking(userId);
    try {
      await markCompletion(props.cohortId, userId, complete);
      await refetch();
    } catch {
      showToast(t(msg`Could not update`), "error");
    } finally {
      setMarking(null);
    }
  };

  return (
    <div class="space-y-2" data-testid="completion-report">
      <Show
        when={report()}
        fallback={<p class="text-sm text-muted-foreground">{t(msg`Loading…`)}</p>}
      >
        {(r) => (
          <div class="overflow-x-auto">
            <table class="w-full text-sm">
              <thead>
                <tr class="text-left text-xs text-muted-foreground">
                  <th class="py-1">{t(msg`Student`)}</th>
                  <th class="py-1">{t(msg`Grade`)}</th>
                  <th class="py-1">{t(msg`Criteria`)}</th>
                  <th class="py-1">{t(msg`Status`)}</th>
                  <th class="py-1" />
                </tr>
              </thead>
              <tbody>
                <For each={r().rows}>
                  {(row) => (
                    <tr
                      class="border-t border-border"
                      data-testid={`completion-report-row-${row.user_id}`}
                    >
                      <td class="py-2">{row.name ?? row.email ?? row.user_id}</td>
                      <td class="py-2 tabular-nums">{row.final_display}</td>
                      <td class="py-2">
                        <div class="flex flex-wrap gap-1">
                          <For each={row.criteria}>
                            {(c) => (
                              <span
                                class={`rounded px-1.5 py-0.5 text-xs ${
                                  c.met
                                    ? "bg-success/15 text-success"
                                    : "bg-muted text-muted-foreground"
                                }`}
                                title={c.detail}
                                data-testid={`completion-cell-${c.key}`}
                              >
                                {c.met ? "✓" : "✗"} {c.key}
                              </span>
                            )}
                          </For>
                        </div>
                      </td>
                      <td class="py-2">
                        <span
                          data-testid={`completion-passed-${row.user_id}`}
                          data-passed={row.passed ? "true" : "false"}
                          class={row.passed ? "text-success" : "text-muted-foreground"}
                        >
                          {row.passed ? t(msg`Passes`) : t(msg`Not yet`)}
                        </span>
                      </td>
                      <td class="py-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={marking() === row.user_id}
                          data-testid={`completion-mark-${row.user_id}`}
                          onClick={() => void toggleMark(row.user_id, true)}
                        >
                          {t(msg`Mark complete`)}
                        </Button>
                      </td>
                    </tr>
                  )}
                </For>
              </tbody>
            </table>
          </div>
        )}
      </Show>
    </div>
  );
};

// ── Composite tab ───────────────────────────────────────────────────────────

export const CompletionTab: Component<{
  cohortId: string;
  organizationId: string;
  courseId?: string;
}> = (props) => {
  const { t } = useI18n();
  const [reloadKey, setReloadKey] = createSignal(0);
  return (
    <FeatureGate
      feature="academy.completion_rules"
      orgId={() => props.organizationId}
      courseId={() => props.courseId}
    >
      <div class="space-y-6" data-testid="completion-tab">
        <section class="space-y-2">
          <h3 class="text-sm font-semibold">{t(msg`Completion rules`)}</h3>
          <CompletionRulesEditor
            cohortId={props.cohortId}
            onSaved={() => setReloadKey((k) => k + 1)}
          />
        </section>
        <section class="space-y-2">
          <h3 class="text-sm font-semibold">{t(msg`Roster completion`)}</h3>
          <CompletionReport cohortId={props.cohortId} reloadKey={reloadKey()} />
        </section>
      </div>
    </FeatureGate>
  );
};
